import { UseQueryResult } from "@tanstack/react-query";
import { queryKeys } from "../../queryKeys";
import { useAppQuery, UseAppQueryOptions } from "../../useAppQuery";

/**
 * Response from the terminology API endpoint.
 */
export interface TerminologyApiResponse {
  hash: string;
  terminology: Record<string, string>;
}

/**
 * Hook to fetch organization-specific terminology overrides.
 * These are merged on top of bundled translations in LocaleProvider.
 *
 * @example
 * const { data, isLoading } = useTerminology();
 * if (data?.terminology) {
 *   applyTerminologyOverrides('en', data.terminology);
 * }
 */
export function useTerminology(
  options?: Omit<UseAppQueryOptions<TerminologyApiResponse, Error>, "queryKey">
): UseQueryResult<TerminologyApiResponse, Error> {
  return useAppQuery<TerminologyApiResponse, Error>("/Locale/terminology", {
    ...options,
    queryKey: queryKeys.locale.terminology(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000 // Keep in cache for 30 minutes
  });
}

/**
 * Hook to fetch terminology hash for cache validation.
 * Lightweight endpoint to check if terminology has changed.
 */
export function useTerminologyHash(
  options?: Omit<UseAppQueryOptions<{ hash: string }, Error>, "queryKey">
): UseQueryResult<{ hash: string }, Error> {
  return useAppQuery<{ hash: string }, Error>("/Locale/terminology/hash", {
    ...options,
    queryKey: queryKeys.locale.terminologyHash(),
    staleTime: 1 * 60 * 1000 // Check every minute
  });
}
